// src/components/auction/ListoneRosterImport.js
// "Carica rose" dell'asta a listone (pulsante accanto a "Crediti iniziali").
// Un solo caricamento aggiorna insieme:
//  - le rose della lega per il tab Lega (export di tutta la lega o rosa CSV
//    di una squadra, stessa logica di prima);
//  - la TUA rosa nel Market: costo d'acquisto = crediti pagati; dal CSV della
//    rosa anche la "Quotazione Attuale", proposta quando rimuovi (vendi) il
//    giocatore. L'export xlsx ha solo nome e prezzo pagato: la tua squadra si
//    riconosce confrontando i nomi con la rosa già presente.
// Niente viene scritto finché non si conferma il riepilogo.
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X, FileSpreadsheet, AlertTriangle, Check } from 'lucide-react';
import {
  parseRosterFile, matchExcelTeamToPlayers, mergeRosterIntoLeague,
} from '../../utils/fantacalcioLeagueRosters';
import { normalizeForMatch } from '../../utils/legheFantacalcioExport';
import { readLeagueRosters } from '../../utils/leagueRostersStore';
import './ListoneRosterImport.css';

const label = (p) => `${p.name} — ${p.team || 's.q.'}`;
const pid = (p) => String(p?.playerId ?? p?.id ?? '');

export default function ListoneRosterImport({ files, auctionId, players = [], myRoster = [], myTeamName, onClose, onApply }) {
  const [loading, setLoading] = useState(true);
  const [parsedFiles, setParsedFiles] = useState([]); // [{ file, data }]
  const [failed, setFailed] = useState([]);
  const [teamKey, setTeamKey] = useState(''); // `${fileIndex}|${team}`
  const [syncRoster, setSyncRoster] = useState(true);
  const [manual, setManual] = useState({}); // excelName → label digitato
  const [removeIds, setRemoveIds] = useState(() => new Set());
  const [applying, setApplying] = useState(false);

  const myIds = useMemo(() => new Set(myRoster.map(pid)), [myRoster]);
  const playerByLabel = useMemo(() => {
    const m = new Map();
    players.forEach((p) => { if (p?.name) m.set(label(p), p); });
    return m;
  }, [players]);

  // 1) Lettura dei file.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = []; const ko = [];
      for (const file of files || []) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const data = await parseRosterFile(file);
          if ((data?.order || []).length) ok.push({ file, data }); else ko.push(file.name);
        } catch { ko.push(file.name); }
      }
      if (!cancelled) { setParsedFiles(ok); setFailed(ko); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [files]);

  // 2) Quale squadra dei file è la tua: per sovrapposizione con la rosa
  //    attuale (giocatori già tuoi), poi per nome squadra. A parità vince la
  //    rosa CSV, che porta anche la quotazione attuale.
  const candidates = useMemo(() => {
    const myNorm = normalizeForMatch(myTeamName || '');
    const out = [];
    parsedFiles.forEach(({ file, data }, fi) => {
      data.order.forEach((team) => {
        const entries = data.teams[team] || [];
        const match = matchExcelTeamToPlayers(entries, players);
        const overlap = match.matched.filter((m) => myIds.has(String(m.player.id))).length;
        const sameName = myNorm && normalizeForMatch(team) === myNorm;
        out.push({ key: `${fi}|${team}`, team, fileName: file.name, kind: data.kind, entries, match, overlap, sameName });
      });
    });
    return out.sort((a, b) => (b.overlap - a.overlap) || (Number(b.sameName) - Number(a.sameName)) || (Number(b.kind === 'single') - Number(a.kind === 'single')));
  }, [parsedFiles, players, myIds, myTeamName]);

  const autoPick = useMemo(() => {
    const best = candidates[0];
    if (!best) return null;
    const convincing = best.sameName || best.overlap >= Math.max(3, Math.min(myIds.size, best.entries.length) * 0.4);
    // Una rosa CSV di un'altra squadra (avversario) non deve toccare la tua.
    return convincing ? best : null;
  }, [candidates, myIds]);

  useEffect(() => {
    if (loading) return;
    setTeamKey(autoPick?.key || '');
    setSyncRoster(Boolean(autoPick));
  }, [loading, autoPick]);

  const chosen = candidates.find((c) => c.key === teamKey) || null;

  // Proposte sicure pre-compilate per i nomi non riconosciuti.
  useEffect(() => {
    if (!chosen) return;
    const g = {};
    chosen.match.unmatched.forEach((u) => { if (u.guess) g[u.name] = label(u.guess); });
    setManual(g);
  }, [chosen]);

  // 3) Riepilogo delle modifiche alla tua rosa.
  const diff = useMemo(() => {
    if (!chosen) return null;
    const rows = [
      ...chosen.match.matched.map((m) => ({ player: m.player, cost: m.cost, currentValue: m.currentValue ?? null })),
      ...chosen.match.unmatched
        .map((u) => ({ player: playerByLabel.get(String(manual[u.name] || '').trim()), cost: u.cost, currentValue: u.currentValue ?? null }))
        .filter((r) => r.player),
    ];
    const seen = new Set();
    const uniq = rows.filter((r) => { const id = String(r.player.id); if (seen.has(id)) return false; seen.add(id); return true; });
    const byId = new Map(myRoster.map((p) => [pid(p), p]));
    const add = []; const update = []; const same = [];
    uniq.forEach((r) => {
      const existing = byId.get(String(r.player.id));
      if (!existing) { add.push(r); return; }
      const costChanged = Number(existing.credits || 0) !== Number(r.cost || 0);
      const curChanged = r.currentValue !== null && Number(existing.currentValue) !== Number(r.currentValue);
      if (costChanged || curChanged) update.push({ ...r, existing, costChanged, curChanged }); else same.push(r);
    });
    const missing = myRoster.filter((p) => !seen.has(pid(p)));
    const unmatchedLeft = chosen.match.unmatched.filter((u) => !playerByLabel.get(String(manual[u.name] || '').trim()));
    return { add, update, same, missing, unmatched: chosen.match.unmatched, unmatchedLeft, hasCurrent: chosen.kind === 'single' };
  }, [chosen, manual, playerByLabel, myRoster]);

  const leagueNext = useMemo(() => {
    let next = readLeagueRosters(auctionId);
    parsedFiles.forEach(({ file, data }) => { next = mergeRosterIntoLeague(next, data, file.name); });
    return next;
  }, [parsedFiles, auctionId]);

  const apply = async () => {
    setApplying(true);
    try {
      await onApply({
        league: leagueNext,
        upserts: syncRoster && diff ? [...diff.add, ...diff.update].map((r) => ({ player: r.player, credits: r.cost, currentValue: r.currentValue })) : [],
        removals: syncRoster && diff ? diff.missing.filter((p) => removeIds.has(pid(p))) : [],
      });
      onClose();
    } finally {
      setApplying(false);
    }
  };

  const name = (p) => p?.playerSnapshot?.name || p?.playerName || p?.name || '—';

  return createPortal(
    <div className="overlay-backdrop lri-backdrop" onClick={() => { if (!applying) onClose(); }}>
      <div className="overlay-panel glass lri" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="lri__head">
          <span className="lri__icon"><FileSpreadsheet size={18} /></span>
          <div>
            <h3>Carica rose</h3>
            <p>Aggiorna il tab Lega e la tua rosa nel Market.</p>
          </div>
          <button type="button" className="lri__close" onClick={onClose} disabled={applying} aria-label="Chiudi"><X size={16} /></button>
        </div>

        {loading && <div className="lri__loading"><Loader2 size={18} className="spin" /> Leggo i file…</div>}

        {!loading && (
          <div className="lri__body">
            {failed.length > 0 && (
              <div className="lri__notice is-err"><AlertTriangle size={14} /> Non ho trovato rose in: {failed.join(', ')}.</div>
            )}

            {parsedFiles.length > 0 && (
              <section className="lri__block">
                <h4>Tab Lega</h4>
                <p>
                  {parsedFiles.map(({ file, data }) => (
                    <span key={file.name} className="lri__file">
                      <b>{file.name}</b> · {data.kind === 'single' ? `rosa di ${data.team} (si aggiunge alle squadre già caricate)` : `${data.order.length} squadre (sostituiscono quelle caricate)`}
                    </span>
                  ))}
                </p>
              </section>
            )}

            {candidates.length > 0 && (
              <section className="lri__block">
                <h4>La tua rosa (Market)</h4>
                <label className="lri__check">
                  <input type="checkbox" checked={syncRoster} onChange={(e) => setSyncRoster(e.target.checked)} />
                  <span>Aggiorna la mia rosa con questi dati</span>
                </label>
                {syncRoster && (
                  <label className="lri__field">
                    <span>La tua squadra nel file</span>
                    <select value={teamKey} onChange={(e) => setTeamKey(e.target.value)}>
                      <option value="">— scegli —</option>
                      {candidates.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.team} · {c.entries.length} giocatori{c.overlap ? ` · ${c.overlap} già nella tua rosa` : ''}{c.sameName ? ' · stesso nome' : ''}
                        </option>
                      ))}
                    </select>
                    {chosen && (
                      <em>
                        {chosen.kind === 'single'
                          ? 'Rosa CSV: prendo il costo d\'acquisto (crediti pagati) e la quotazione attuale, che ti proporrò quando rimuovi il giocatore.'
                          : 'Export della lega: c\'è solo il prezzo pagato, che ti proporrò anche quando rimuovi il giocatore.'}
                      </em>
                    )}
                  </label>
                )}
                {syncRoster && !chosen && <div className="lri__notice"><AlertTriangle size={14} /> Non ho riconosciuto la tua squadra: sceglila dal menu.</div>}
              </section>
            )}

            {syncRoster && diff && (
              <>
                <div className="lri__summary">
                  <span className="is-add"><b>{diff.add.length}</b> da aggiungere</span>
                  <span className="is-upd"><b>{diff.update.length}</b> da aggiornare</span>
                  <span><b>{diff.same.length}</b> invariati</span>
                  {diff.missing.length > 0 && <span className="is-miss"><b>{diff.missing.length}</b> non nel file</span>}
                </div>

                {diff.unmatched.length > 0 && (
                  <section className="lri__block">
                    <h4>Nomi da abbinare ({diff.unmatchedLeft.length} ancora senza giocatore)</h4>
                    <datalist id="lri-players">{players.map((p) => <option key={p.id} value={label(p)} />)}</datalist>
                    {diff.unmatched.map((u) => (
                      <div key={u.name} className="lri__match">
                        <span><b>{u.name}</b> · {u.cost} cr</span>
                        <input
                          list="lri-players"
                          placeholder="Cerca giocatore… (vuoto = non importare)"
                          value={manual[u.name] || ''}
                          onChange={(e) => setManual((m) => ({ ...m, [u.name]: e.target.value }))}
                        />
                      </div>
                    ))}
                  </section>
                )}

                {diff.add.length > 0 && (
                  <section className="lri__block">
                    <h4>Da aggiungere</h4>
                    <ul className="lri__list">
                      {diff.add.map((r) => (
                        <li key={r.player.id}><Check size={12} /> {r.player.name} <em>{r.cost} cr{r.currentValue !== null ? ` · att. ${r.currentValue}` : ''}</em></li>
                      ))}
                    </ul>
                  </section>
                )}

                {diff.update.length > 0 && (
                  <section className="lri__block">
                    <h4>Da aggiornare</h4>
                    <ul className="lri__list">
                      {diff.update.map((r) => (
                        <li key={r.player.id}>
                          {r.player.name}
                          <em>
                            {r.costChanged ? `pagato ${r.existing.credits} → ${r.cost} cr` : `pagato ${r.cost} cr`}
                            {r.currentValue !== null ? ` · attuale ${r.currentValue}` : ''}
                          </em>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {diff.missing.length > 0 && (
                  <section className="lri__block">
                    <h4>In rosa ma non nel file</h4>
                    <p className="lri__hint">Spunta chi vuoi togliere dalla rosa (nessuna plus/minusvalenza: rientrano i crediti pagati).</p>
                    <ul className="lri__list">
                      {diff.missing.map((p) => (
                        <li key={pid(p)}>
                          <label className="lri__check">
                            <input
                              type="checkbox"
                              checked={removeIds.has(pid(p))}
                              onChange={(e) => setRemoveIds((s) => { const n = new Set(s); if (e.target.checked) n.add(pid(p)); else n.delete(pid(p)); return n; })}
                            />
                            <span>{name(p)} <em>{p.credits} cr</em></span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}
          </div>
        )}

        <div className="lri__actions">
          <button type="button" className="lri__btn" onClick={onClose} disabled={applying}>Annulla</button>
          <button
            type="button"
            className="lri__btn lri__btn--primary"
            onClick={apply}
            disabled={loading || applying || !parsedFiles.length || (syncRoster && !chosen)}
          >
            {applying ? 'Salvo…' : 'Applica'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
